import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../features/theme/context/ThemeContext';
import midusFullTightBlack from '../assets/midus_full_tight_black.svg';
import midusFullTightWhite from '../assets/midus_full_tight_white.svg';
import NvgtrShimmerLoader from '../features/chat/components/NvgtrShimmerLoader';
import { navigateToStatusHome, getPublicStatusHref } from '../utils/publicStatusPage';

const ManageSubscriptionPage = () => {
  const { darkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [subscribedServices, setSubscribedServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const token = searchParams.get('token');

  const getApiUrl = useCallback((endpoint) => {
    const apiUrl = process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'development' ? 'http://192.168.86.56:8081' : 'https://api.midus.co');
    let baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    if (baseUrl.endsWith('/api')) {
      const cleanEndpoint = endpoint.startsWith('api/') ? endpoint.substring(4) : endpoint;
      return `${baseUrl}/${cleanEndpoint}`;
    }
    const cleanEndpoint = endpoint.startsWith('api/') ? endpoint : `api/${endpoint}`;
    return `${baseUrl}/${cleanEndpoint}`;
  }, []);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!token) {
        setError('Invalid link. No token provided.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(getApiUrl(`status/subscription?token=${token}`));
        const data = await response.json();

        if (data.success) {
          setSubscription({ email: data.email, isActive: data.isActive });
          setSubscribedServices(data.subscribedServices || ['apis', 'midus', 'compliance']);
        } else {
          setError(data.message || 'Invalid or expired link.');
        }
      } catch (err) {
        console.error('Subscription fetch error:', err);
        setError('Failed to load subscription. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [token, getApiUrl]);

  const handleUnsubscribe = () => {
    const href = getPublicStatusHref('unsubscribe', `token=${encodeURIComponent(token)}`);
    if (href.startsWith('http')) {
      window.location.href = href;
    } else {
      navigate(href);
    }
  };

  const handleSaveServices = async () => {
    if (subscribedServices.length === 0) return;
    setSaving(true);
    setSaveMessage('');
    try {
      const response = await fetch(getApiUrl('status/subscription/services'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, services: subscribedServices }),
      });
      const data = await response.json();
      if (data.success) {
        setSaveMessage('Preferences saved.');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        setSaveMessage(data.message || 'Failed to save.');
      }
    } catch (err) {
      console.error('Save services error:', err);
      setSaveMessage('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleGoToStatus = () => {
    navigateToStatusHome(navigate);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-[#111111]">
        <NvgtrShimmerLoader darkMode={darkMode} size={60} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#111111] flex flex-col">
      {/* Header */}
      <div className="border-b border-[#d1d5db] dark:border-[#282828]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="https://heymidus.com" className="hover:opacity-80 transition-opacity">
              <img
                src={darkMode ? midusFullTightWhite : midusFullTightBlack}
                alt="Midus"
                className="h-5"
              />
            </a>
            <button
              onClick={handleGoToStatus}
              className="text-xs text-[#6b7280] dark:text-[#9ca3af] hover:text-[#181818] dark:hover:text-white transition-colors"
            >
              Current Status
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 max-w-4xl mx-auto px-4 py-12 w-full">
        {error ? (
          <div className="w-full text-center space-y-6">
            <h1 className="text-xl font-semibold text-[#181818] dark:text-white">
              Invalid Link
            </h1>
            <p className="text-[#6b7280] dark:text-[#9ca3af]">{error}</p>
            <button
              onClick={handleGoToStatus}
              className="px-4 py-2 bg-[#181818] dark:bg-white text-white dark:text-[#181818] rounded-xl hover:opacity-90 transition-opacity"
            >
              Go to Status Page
            </button>
          </div>
        ) : subscription ? (
          <div className="w-full">
            <h1 className="text-xl font-semibold text-[#181818] dark:text-white mb-6">
              Manage Subscription
            </h1>
            <div className="bg-white dark:bg-[#181818] rounded-2xl border border-[#d1d5db] dark:border-[#282828] p-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-medium text-[#6b7280] dark:text-[#9ca3af] mb-1">
                    Email
                  </label>
                  <p className="text-lg font-semibold text-[#181818] dark:text-white">
                    {subscription.email}
                  </p>
                </div>

                {subscription.isActive && (
                  <div>
                    <label className="block text-sm font-medium text-[#181818] dark:text-white mb-2">
                      Services
                    </label>
                    <div className="space-y-2">
                      {[
                        { key: 'apis', label: 'APIs' },
                        { key: 'midus', label: 'Midus' },
                        { key: 'compliance', label: 'Midus for Compliance' }
                      ].map(({ key, label }) => {
                        const isChecked = subscribedServices.includes(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              setSubscribedServices(prev =>
                                prev.includes(key)
                                  ? prev.filter(s => s !== key)
                                  : [...prev, key]
                              );
                              setSaveMessage('');
                            }}
                            disabled={saving}
                            className={`w-full flex items-center px-3 py-2.5 rounded-lg border transition-colors text-left ${
                              isChecked
                                ? 'border-[#9ca3af] dark:border-[#6b7280] bg-white dark:bg-[#111111]'
                                : 'border-[#d1d5db] dark:border-[#282828] bg-white dark:bg-[#111111]'
                            } hover:border-[#9ca3af] dark:hover:border-[#6b7280] disabled:opacity-50`}
                          >
                            <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                              isChecked
                                ? 'bg-[#181818] dark:bg-white border-[#181818] dark:border-white'
                                : 'border-[#d1d5db] dark:border-[#282828] bg-transparent'
                            }`}>
                              {isChecked && (
                                <svg className="w-2.5 h-2.5 text-white dark:text-[#181818]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className="ml-3 text-sm text-[#181818] dark:text-white">{label}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-4 flex items-center space-x-3">
                      <button
                        onClick={handleSaveServices}
                        disabled={saving || subscribedServices.length === 0}
                        className="px-4 py-2 text-sm font-medium text-white dark:text-black bg-black dark:bg-white rounded-lg hover:bg-black/80 dark:hover:bg-white/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      {saveMessage && (
                        <span className="text-sm text-[#6b7280] dark:text-[#9ca3af]">{saveMessage}</span>
                      )}
                    </div>
                  </div>
                )}

                {subscription.isActive ? (
                  <a
                    href={getPublicStatusHref('unsubscribe', `token=${encodeURIComponent(token)}`)}
                    onClick={(e) => {
                      e.preventDefault();
                      handleUnsubscribe();
                    }}
                    className="inline-block text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
                  >
                    Unsubscribe from all updates
                  </a>
                ) : (
                  <p className="text-sm text-[#6b7280] dark:text-[#9ca3af]">
                    You are not currently subscribed to status updates.
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="border-t border-[#d1d5db] dark:border-[#282828] mt-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <p className="text-sm text-[#6b7280] dark:text-[#9ca3af]">
            Questions? Contact us at{' '}
            <a
              href="mailto:support@heymidus.com"
              className="text-[#FFBDA3] hover:text-[#FFBDA3] dark:text-[#FFBDA3] dark:hover:text-[#FFBDA3]"
            >
              support@heymidus.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ManageSubscriptionPage;
